import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { TabView, TabPanel } from "primereact/tabview";
import { Timeline } from "primereact/timeline";
import { Dialog } from "primereact/dialog";
import { Sidebar } from "primereact/sidebar";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Chart } from "primereact/chart";
import { useParams, useNavigate, useLocation} from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import SupplierService from "../../services/SupplierService";
import ChequeService from "../../services/ChequeService";
import PromissoryNoteService from "../../services/PromissoryNoteService";
import FinancialAccountService from "../../services/FinancialAccountService";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";

const HistoryTimeline = ({ timelineEvents }) => {
  if (timelineEvents.length === 0) {
    return (
      <div className="text-center py-12 !text-slate-400 italic text-sm font-medium">
        Henüz işlem bulunmuyor.
      </div>
    );
  }

  return (
    <Timeline
      value={timelineEvents}
      pt={{
        marker: {
          className: 'flex items-center justify-center shadow-none border-none bg-transparent',
          style: { width: '80px' }
        },
        opposite: {
          className: 'hidden'
        }
      }}
      marker={(item) => (
        <div
          className="flex items-center justify-center rounded-2xl shadow-lg border-2 border-white ring-2 ring-slate-100 shrink-0"
          style={{
            backgroundColor: item.color,
            width: '45px',
            height: '45px',
            minWidth: '45px',
            minHeight: '45px'
          }}
        >
          <i className={`${item.icon} text-white text-lg`} />
        </div>
      )}
      content={(item) => (
        <div className="mb-3 ml-1 flex-1">
          <div className="font-bold text-slate-800 text-sm mb-1">{item.status}</div>
          <p className="m-0 text-xs text-slate-500 mb-1">{item.details}</p>

          <div className="flex justify-between items-center bg-slate-50 px-2 py-1 rounded-xl border border-slate-100 gap-2 shadow-sm">
            <span className={`text-[11px] font-black px-3 py-1.5 rounded-lg uppercase shrink-0 ${
              item.type === 'payment' ? '!bg-emerald-500 text-white' : '!bg-blue-600 text-white'
            }`}>
              {item.amount.toLocaleString()} ₺
            </span>
            <div className="flex items-center gap-2 text-slate-400">
              <i className="pi pi-clock text-[10px]"></i>
              <small className="font-bold text-[10px]">{item.date}</small>
            </div>
          </div>
        </div>
      )}
    />
  );
};

export default function SupplierDetail() {
  const { id } = useParams();
  const supplierId = Number(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState(false);
  const [returnHistory, setReturnHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const location = useLocation();
  const toast = useRef(null);
  const paymentAmountRef = useRef(null);

  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Nakit");
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentDescription, setPaymentDescription] = useState("");
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const [detailDialogVisible, setDetailDialogVisible] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const menuRef = useRef(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const filteredInvoiceHistory = useMemo(() => {
    if (!showOnlyUnpaid) return invoiceHistory;
    return invoiceHistory.filter(inv => inv.isDueTrackingActive && inv.remainingAmount > 0);
  }, [invoiceHistory, showOnlyUnpaid]);

  const invoiceMenuItems = useMemo(() => {
    if (!selectedInvoice) return [];

    const items = [
      {
        label: 'Detay',
        icon: 'pi pi-search',
        command: () => handleShowDetail(selectedInvoice.id)
      },
      {
        label: 'Düzenle',
        icon: 'pi pi-pencil',
        command: () => navigate(`/suppliers/purchase/edit/${selectedInvoice.id}`)
      }
    ];

    if (selectedInvoice.isDueTrackingActive && selectedInvoice.remainingAmount > 0) {
      items.push({
        label: 'Ödeme Yap',
        icon: 'pi pi-wallet',
        command: () => {
          setPaymentMethod("Nakit");
          setUseExistingDocument(false);
          setSelectedDocument(null);
          setPaymentAmount(selectedInvoice.remainingAmount);
          setPaymentDescription(`${selectedInvoice.invoiceNo} nolu fatura ödemesi`);
          setPaymentDialogVisible(true);
        }
      });
    }

    return items;
  }, [selectedInvoice, navigate]);

  // Return Product State
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [mounted, setMounted] = useState(false);
  const [selectedReturnRow, setSelectedReturnRow] = useState(null);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const tooltipRef = useRef(null);
  
  // Dynamic Sidebar Resizing
  const [sidebarWidth, setSidebarWidth] = useState(720);
  const isResizing = useRef(false);

  const handleResizing = useCallback((e) => {
    if (!isResizing.current) return;
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= 450 && newWidth <= window.innerWidth * 0.95) {
      setSidebarWidth(newWidth);
    }
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "default";
    document.body.style.userSelect = "auto";
  }, [handleResizing]);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [handleResizing, stopResizing]);

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleResizing);
      document.removeEventListener("mouseup", stopResizing);
    };
  }, [handleResizing, stopResizing]);

  const paymentMethods = [
    { label: "Nakit", value: "Nakit", icon: "pi-money-bill", desc: "Kasadan anlık nakit çıkışı", category: "Likit" },
    { label: "Banka Transferi", value: "Banka Transferi", icon: "pi-send", desc: "Hesaptan Havale/EFT gönderimi", category: "Likit" },
    { label: "Kredi Kartı", value: "Kredi Kartı", icon: "pi-credit-card", desc: "Şirket kredi kartı ile ödeme", category: "Dijital" },
    { label: "Çek (Kendi Çekimiz)", value: "Çek", icon: "pi-id-card", desc: "Şirket çeki ile vadeli ödeme", category: "Evrak" },
    { label: "Senet (Kendi Senedimiz)", value: "Senet", icon: "pi-file-edit", desc: "Kendi senedimiz ile borçlanma", category: "Evrak" },
    { label: "Çek Cirosu", value: "ChequeEndorse", icon: "pi-sync", desc: "Müşteriden alınan çeki devret", category: "Evrak" },
    { label: "Senet Cirosu", value: "PromissoryEndorse", icon: "pi-sync", desc: "Müşteriden alınan senedi devret", category: "Evrak" }
  ];

  const [portfolioCheques, setPortfolioCheques] = useState([]);
  const [paymentFilters, setPaymentFilters] = useState({
    global: { value: null, matchMode: "contains" },
    date: { value: null, matchMode: "contains" },
    description: { value: null, matchMode: "contains" },
    paymentMethod: { value: null, matchMode: "equals" }
  });
  const [portfolioNotes, setPortfolioNotes] = useState([]);
  const [ownPortfolioCheques, setOwnPortfolioCheques] = useState([]);
  const [ownPortfolioNotes, setOwnPortfolioNotes] = useState([]);
  const [useExistingDocument, setUseExistingDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const filteredAccounts = useMemo(() => {
    if (paymentMethod === "Nakit") return financialAccounts.filter(a => a.accountType === 1);
    if (paymentMethod === "Banka Transferi" || paymentMethod === "ChequeEndorse" || paymentMethod === "PromissoryEndorse" || paymentMethod === "Havale" || paymentMethod === "EFT") return financialAccounts.filter(a => a.accountType === 2);
    if (paymentMethod === "Kredi Kartı") return financialAccounts.filter(a => a.accountType === 3);
    return [];
  }, [financialAccounts, paymentMethod]);

  useEffect(() => {
    if (filteredAccounts.length === 1) {
      setSelectedAccountId(filteredAccounts[0].id);
      if (showEditPaymentDialog && editingPayment) {
        setEditingPayment(prev => ({ ...prev, financialAccountId: filteredAccounts[0].id }));
      }
    } else {
      setSelectedAccountId(null);
    }
  }, [filteredAccounts, showEditPaymentDialog, editingPayment]);

  const [chequeDetails, setChequeDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: ""
  });

  const [promissoryDetails, setPromissoryDetails] = useState({
    dueDate: null,
    drawer: "",
    guarantor: ""
  });

const formatDateOnly = (date) => {
  if (!date) return null;

  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
  useEffect(() => {
    if (paymentMethod === "ChequeEndorse") {
      // status: 1 (Portfolio), type: 1 (Inbound - Customer document)
      ChequeService.getCheques(1, 1).then(res => setPortfolioCheques(res.data || []));
    } else if (paymentMethod === "PromissoryEndorse") {
      // status: 1 (Portfolio), type: 1 (Inbound - Customer document)
      PromissoryNoteService.getPromissoryNotes(1, 1).then(res => setPortfolioNotes(res.data || []));
    } else if (paymentMethod === "Çek") {
      // status: 1 (Portfolio), type: 2 (Outbound - Our document)
      ChequeService.getCheques(1, 2).then(res => setOwnPortfolioCheques(res.data || []));
    } else if (paymentMethod === "Senet") {
      // status: 1 (Portfolio), type: 2 (Outbound - Our document)
      PromissoryNoteService.getPromissoryNotes(1, 2).then(res => setOwnPortfolioNotes(res.data || []));
    }
  }, [paymentMethod]);
 useEffect(() => {
  if (!toast.current) return;

  if (location.state?.from === "purchase-create") {
    toast.current.show({
      severity: "success",
      summary: "Alış kaydı eklendi",
      detail: "Stok ve cari güncellendi",
      life: 2500,
    });
  }
}, [location.state]);
  /* =====================
     FETCH DATA
  ===================== */
  const fetchSupplierDetail = useCallback(async () => {
    if (!supplierId) return;
    try {

      const data = await SupplierService.getSupplierDetail(supplierId);

      setSupplier(data.supplier);
      setPurchaseHistory(data.purchaseHistory || []);
      setInvoiceHistory(data.invoiceHistory || []);
      setReturnHistory(data.returnHistory || []);
      setPaymentHistory(data.payments || []);
      setUpcomingPayments(data.upcomingPayments || []);
      setTimelineEvents(data.transactions || []);
      setSummaryData(data.summary);
    } catch (error) {
      console.error("Supplier detail error:", error);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    setMounted(true);
    fetchSupplierDetail();
    FinancialAccountService.getAll().then(res => {
      if (res.success) {
        setFinancialAccounts(res.data || []);
      }
    });
  }, [fetchSupplierDetail]);
 
  /* =====================
     PAYMENT LOGIC
  ===================== */
  const handlePayment = async () => {
    if (paymentAmount <= 0 && paymentMethod !== "ChequeEndorse" && paymentMethod !== "PromissoryEndorse" && !useExistingDocument) return;

    try {
      setSubmittingPayment(true);

      let res;
      if (paymentMethod === "ChequeEndorse" || (paymentMethod === "Çek" && useExistingDocument)) {
        if (!selectedDocument) return;
        res = await ChequeService.endorseCheque({
          chequeId: selectedDocument.id,
          supplierId: supplierId,
          endorsementDate: formatDateOnly(paymentDate),
          description: paymentDescription,
          financialAccountId: selectedAccountId
        });
      } else if (paymentMethod === "PromissoryEndorse" || (paymentMethod === "Senet" && useExistingDocument)) {
        if (!selectedDocument) return;
        res = await PromissoryNoteService.endorsePromissoryNote({
          promissoryNoteId: selectedDocument.id,
          supplierId: supplierId,
          endorsementDate: formatDateOnly(paymentDate),
          description: paymentDescription,
          financialAccountId: selectedAccountId
        });
      } else {
        const payload = {
          supplierId: supplierId,
          amount: paymentAmount,
          paymentMethod: paymentMethod,
          financialAccountId: selectedAccountId,
          paymentDate: formatDateOnly(paymentDate),
          description: paymentDescription,
          ...(paymentMethod === "Çek" ? chequeDetails : {}),
          ...(paymentMethod === "Senet" ? promissoryDetails : {})
        };
        res = await SupplierService.receivePayment(payload);
      }
      if (res.success) {
        setPaymentDialogVisible(false);
        setPaymentAmount(0);
        setPaymentDescription("");
        await fetchSupplierDetail();
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Ödeme başarıyla kaydedildi.",
          life: 3000,
        });
      }
      else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: "Ödeme kaydedilemedi.",
          life: 3000,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || editingPayment.amount <= 0) return;

    try {
      setSubmittingPayment(true);

      const toIsoString = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return d.toISOString();
      };

      const res = await SupplierService.updatePayment({
        transactionId: Number(editingPayment.id),
        amount: Number(editingPayment.amount),
        paymentMethod: String(editingPayment.paymentMethod || "Nakit"),
        financialAccountId: editingPayment.financialAccountId ? Number(editingPayment.financialAccountId) : null,
        paymentDate: toIsoString(editingPayment.paymentDate),
        description: String(editingPayment.description || ""),
        bankName: editingPayment.bankName ? String(editingPayment.bankName) : null,
        bankBranch: editingPayment.bankBranch ? String(editingPayment.bankBranch) : null,
        checkNumber: editingPayment.checkNumber ? String(editingPayment.checkNumber) : null,
        dueDate: editingPayment.dueDate ? toIsoString(editingPayment.dueDate) : null,
        drawer: editingPayment.drawer ? String(editingPayment.drawer) : null,
        guarantor: editingPayment.guarantor ? String(editingPayment.guarantor) : null
      });

      if (res.success) {
        setShowEditPaymentDialog(false);
        setEditingPayment(null);
        await fetchSupplierDetail();
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Ödeme başarıyla güncellendi.",
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "Ödeme güncellenemedi.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.current.show({
        severity: "error",
        summary: "Sistem Hatası",
        detail: error.response?.data?.message || error.message || "Ödeme güncellenirken bir hata oluştu.",
        life: 5000,
      });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleDeleteReturn = (record) => {
    confirmDialog({
        message: 'Bu iade işlemini iptal etmek istediğinize emin misiniz? Stoklar ve tedarikçi bakiyesi geri alınacaktır.',
        header: 'İade İptal Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, İptal Et',
        rejectLabel: 'Vazgeç',
        accept: async () => {
            try {
                const res = await SupplierService.deleteReturn(record.id);
                if (res.success) {
                    toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İade iptal edildi', life: 3000 });
                    await fetchSupplierDetail();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message || 'İade iptal edilemedi', life: 3000 });
                }
            } catch (err) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İade iptal edilirken hata oluştu', life: 3000 });
            }
        }
    });
  };

  const handleDeletePayment = (record) => {
    const isDocument = record.referenceType === "CHEQUE" || record.referenceType === "PROMISSORY_NOTE";
    confirmDialog({
        message: isDocument ? "Çekin/Senetin verildiği tedarikçi silinecektir onaylıyor musunuz?" : 'Bu ödeme işlemini silmek istediğinize emin misiniz?',
        header: 'Silme Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, Sil',
        rejectLabel: 'Vazgeç',
        accept: async () => {
            try {
                const res = await SupplierService.deletePayment(record.id);
                if (res.success) {
                    toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İşlem silindi', life: 3000 });
                    await fetchSupplierDetail();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message || 'İşlem silinemedi', life: 3000 });
                }
            } catch (err) {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem silinirken hata oluştu', life: 3000 });
            }
        }
    });
  };

  const handleShowDetail = async (id) => {
    try {
      setLoadingDetail(true);
      setDetailDialogVisible(true);
      const res = await SupplierService.getPurchaseTransactionDetail(id);
      if (res.success) {
        setInvoiceDetail(res.data);
      }
    } catch (error) {
      console.error("Error fetching invoice detail:", error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReturnProduct = (rowData) => {
    // Check if the whole invoice will become empty
    const rowProductId = rowData.productId || rowData.ProductId;
    const rowTransactionId = rowData.transactionId || rowData.TransactionId;

    const sameInvoiceProducts = purchaseHistory.filter(p => (p.transactionId || p.TransactionId) === rowTransactionId);
    const totalQtyInInvoice = sameInvoiceProducts.reduce((sum, p) => sum + p.quantity, 0);
    const willBeEmpty = (totalQtyInInvoice - returnQuantity) === 0;

    const proceedReturn = async () => {
      try {
        setSubmittingReturn(true);
        const res = await SupplierService.createPurchaseReturnFromLine({
          purchaseTransactionId: rowTransactionId,
          productId: rowProductId,
          quantity: returnQuantity,
          description: "Alıştan İade"
        });
        if (res.data?.success || res.success) {
          setShowReturnDialog(false);
          toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İade işlemi kaydedildi', life: 3000 });
          await fetchSupplierDetail();
        } else {
          toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message || 'İade alınırken hata oluştu', life: 3000 });
        }
      } catch (err) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İade alınırken hata oluştu', life: 3000 });
      } finally {
        setSubmittingReturn(false);
      }
    };

    if (willBeEmpty) {
      confirmDialog({
        message: 'Faturada ürün kalmayacak onaylıyor musunuz?',
        header: 'Kritik İade Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, Faturayı İptal Et ve İade Al',
        rejectLabel: 'Vazgeç',
        accept: proceedReturn
      });
    } else {
      confirmDialog({
        message: `${rowData.productName} ürününden ${returnQuantity} adet iade etmek istediğinize emin misiniz?`,
        header: 'İade Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, İade Et',
        rejectLabel: 'Vazgeç',
        accept: proceedReturn
      });
    }
  };

  /* =====================
     GUARDS
  ===================== */
  if (loading || !supplier) {
    return (
      <div className="p-4">
        <Skeleton height="4rem" className="mb-4 rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-4"><Skeleton height="20rem" className="rounded-xl" /></div>
          <div className="md:col-span-8"><Skeleton height="20rem" className="rounded-xl" /></div>
        </div>
      </div>
    );
  }

  // Chart Data (Mocking for now as we don't have historical monthly data yet)
  const chartData = {
    labels: ["Oca", "Şub", "Mar", "Nis", "May", "Haz"],
    datasets: [
      {
        label: "Alış Tutarı (₺)",
        data: [0, 0, 0, 0, 0, summaryData?.totalPurchaseAmount || 0],
        borderColor: "#42A5F5",
        tension: 0.4,
        fill: false,
      },
    ],
  };

  

  const chartOptions = {
    plugins: { legend: { display: false } },
    maintainAspectRatio: false,
  };

  return (
    <div className="p-2 space-y-2 animate-fade-in">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
      <Menu model={invoiceMenuItems} popup ref={menuRef} id="invoice_menu" appendTo={mounted ? document.body : "self"} />
      <ConfirmDialog />
      <Tooltip
        ref={tooltipRef}
        appendTo={document.body}
        position="top"
        baseZIndex={99999}
      />
      {/* Ürün İade Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <i className="pi pi-refresh text-orange-600 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Ürün İade Et</h3>
              <p className="text-xs text-slate-500 font-medium m-0">İade edilecek miktar bilgisini girin</p>
            </div>
          </div>
        }
        visible={showReturnDialog}
        style={{ width: '400px' }}
        onHide={() => setShowReturnDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowReturnDialog(false)} />
            <Button label="İadeyi Onayla" icon="pi pi-check" className="!bg-orange-600 border-none font-black" onClick={() => handleReturnProduct(selectedReturnRow)} disabled={!returnQuantity || returnQuantity <= 0 || submittingReturn} />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          {selectedReturnRow && (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 mb-2">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">ÜRÜN</div>
              <div className="text-sm font-bold text-slate-700">{selectedReturnRow.productName}</div>
              <div className="mt-2 flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500">Maksimum İade Edilebilir:</span>
                <span className="text-xs font-black text-slate-900">{selectedReturnRow.quantity} Adet</span>
              </div>
            </div>
          )}
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İade Adedi</label>
            <InputNumber
              value={returnQuantity}
              onValueChange={(e) => setReturnQuantity(e.value)}
              min={1}
              max={selectedReturnRow?.quantity}
              placeholder="Miktar giriniz..."
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </Dialog>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-stretch">

        {/* LEFT - SUPPLIER INFO CARD */}
        <div className="lg:col-span-3">
          <div className="h-full bg-white shadow-sm border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
            <div className="bg-slate-900 p-6 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="rounded-xl flex items-center justify-center w-14 h-14 bg-blue-500/20 border border-blue-500/30">
                  <i className="pi pi-building text-2xl text-blue-400"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="m-0 text-xl font-bold truncate">{supplier.name}</h2>
                  <div className="text-slate-400 text-xs font-semibold mt-1">ID: #{supplier.id}</div>
                </div>
              </div>
              <div className={`inline-flex px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
                supplier.balance < 0 ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              }`}>
                {supplier.balance < 0 ? "Borçlu" : "Alacaklı"}
              </div>
            </div>

            <div className="p-6 space-y-6 flex-1">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-phone text-blue-500"></i> Telefon
                  </span>
                  <div className="text-slate-700 font-bold">{supplier.phoneNumber || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-envelope text-blue-500"></i> Email
                  </span>
                  <div className="text-slate-700 font-bold truncate">{supplier.email || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-id-card text-blue-500"></i> Vergi No
                  </span>
                  <div className="text-slate-700 font-bold">{supplier.taxNumber || 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-400 text-xs font-bold uppercase flex items-center gap-2">
                    <i className="pi pi-map-marker text-blue-500"></i> Adres
                  </span>
                  <div className="text-slate-700 font-bold text-sm leading-relaxed">{supplier.address || 'Adres belirtilmedi'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER - FINANCIAL SUMMARY */}
        <div className="lg:col-span-6">
          <div className="h-full bg-white shadow-sm border border-slate-200 rounded-2xl p-4 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h4 className="flex items-center gap-2 m-0 text-slate-800 font-bold">
                <i className="pi pi-chart-pie !text-blue-500"></i> Finansal Durum
              </h4>
            </div>

            <div className="text-center  bg-slate-50 rounded-2xl py-2 border border-slate-100  mb-4">
              <span className="text-slate-600 uppercase tracking-widest text-[12px] font-black block mt-2 mb-2">GÜNCEL BAKİYE</span>
              <h2 className={`m-0 text-5xl font-black ${supplier.balance < 0 ? '!text-red-600' : '!text-emerald-600'}`}>
                {supplier.balance.toLocaleString("tr-TR")} ₺
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4  mt-4">
              <div className="text-center group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Toplam Alış</div>
                <div className="text-slate-800 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.totalPurchaseAmount.toLocaleString()} ₺</div>
              </div>
              <div className="text-center border-l border-slate-100 group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Alış Sayısı</div>
                <div className="text-slate-800 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.totalPurchaseCount}</div>
              </div>
              <div className="text-center border-l border-slate-100 group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Toplam Adet</div>
                <div className="text-slate-800 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.totalQuantity}</div>
              </div>
              <div className="text-center border-l border-slate-100 group">
                <div className="text-slate-400 text-[10px] font-black mb-1 uppercase tracking-wider">Ort. Fiyat</div>
                <div className="text-slate-800 font-bold text-lg group-hover:text-blue-600 transition-colors">{summaryData.avgUnitPrice.toLocaleString()} ₺</div>
              </div>
            </div>
            </div>

            

            <div className="flex-1 min-h-[200px] mb-8">
              <Chart type="line" data={chartData} options={chartOptions} height="100%" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button label="Ödeme Yap" icon="pi pi-wallet" className="bg-emerald-600 border-none hover:bg-emerald-700 rounded-xl py-3.5 font-bold shadow-sm transition-all" onClick={() => {
                setPaymentMethod("Nakit");
                setUseExistingDocument(false);
                setSelectedDocument(null);
                setPaymentAmount(0);
                setPaymentDialogVisible(true);
              }} />
              <Button label="Ürün Al" icon="pi pi-shopping-cart" className="!bg-blue-600 border-none hover:!bg-blue-700 rounded-xl py-3.5 font-bold shadow-sm transition-all" onClick={() => navigate("/suppliers/purchase", { state: { supplierId, supplierName: supplier.name } })} />
              <Button label="İade Gir" icon="pi pi-refresh" className="!bg-orange-600 border-none hover:!bg-orange-700 rounded-xl py-3.5 font-bold shadow-sm transition-all" onClick={() => navigate("/suppliers/return", { state: { supplierId, supplierName: supplier.name } })} />
              <Button label="Ekstre" icon="pi pi-file" className="bg-slate-100 text-slate-700 border-none hover:bg-slate-200 rounded-xl py-3.5 font-bold shadow-sm transition-all" />
            </div>
          </div>
        </div>

        {/* RIGHT - TRANSACTION HISTORY (TIMELINE) or UPCOMING PAYMENTS */}
        <div className="lg:col-span-3">
          <div className="h-full bg-white shadow-sm border !border-slate-200 rounded-2xl overflow-hidden flex flex-col">
            {supplier?.useDueTracking ? (
              <TabView className="h-full flex flex-col pt-2" panelContainerClassName="flex-1 overflow-hidden p-0">
                <TabPanel header={<span><i className="pi pi-calendar-plus mr-2 text-xs"></i>Yaklaşanlar</span>} className="h-full flex flex-col">
                  <div className="flex-1 overflow-y-auto px-3 py-4 custom-scrollbar">
                    {upcomingPayments.length === 0 ? (
                      <div className="text-center py-12 !text-slate-400 italic text-sm font-medium">
                        Bekleyen ödeme bulunmuyor.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcomingPayments.map((item) => (
                          <div key={item.id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:border-blue-400 transition-colors cursor-default group">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.transactionDate}</span>
                              <Tag value={item.status} severity={item.status === 'Açık' ? 'danger' : 'warning'} className="text-[8px] font-black" />
                            </div>
                            <div className="text-xs font-bold text-slate-700 mb-2 leading-tight group-hover:text-blue-600">{item.description}</div>
                            <div className="flex justify-between items-end border-t border-slate-200 pt-2">
                              <div className="flex flex-col">
                                <span className="text-[8px] font-black text-slate-400 uppercase">VADE TARİHİ</span>
                                <span className={`text-[10px] font-black ${new Date(item.dueDate.split('.').reverse().join('-')) < new Date() ? 'text-red-600' : 'text-slate-600'}`}>
                                  {item.dueDate}
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="text-[8px] font-black text-slate-400 uppercase block">KALAN</span>
                                <span className="text-sm font-black text-slate-900">₺{item.remainingAmount.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabPanel>
                <TabPanel header={<span><i className="pi pi-history mr-2 text-xs"></i>Geçmiş</span>} className="h-full flex flex-col">
                  <div className="flex-1 overflow-y-auto pr-6 custom-scrollbar px-3 py-4">
                    <HistoryTimeline timelineEvents={timelineEvents} />
                  </div>
                </TabPanel>
              </TabView>
            ) : (
              <div className="p-3 flex flex-col h-full">
                <h4 className="flex items-center gap-2 m-0 mb-4 text-slate-800 font-bold">
                  <i className="pi pi-history !text-blue-500"></i> İşlem Geçmişi
                </h4>
                <div className="flex-1 overflow-y-auto pr-6 custom-scrollbar">
                   <HistoryTimeline timelineEvents={timelineEvents} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* PURCHASE HISTORY TABBED TABLE */}
        <div className="lg:col-span-8">
          <Card className="p-0 overflow-hidden">
            <TabView>
              <TabPanel header={<span><i className="pi pi-box mr-2"></i>Ürün Bazlı Alışlar</span>}>
                <div className="bg-white  overflow-hidden">
                  <DataTable
                    value={purchaseHistory}
                    paginator rows={10}
                    emptyMessage="Alış kaydı bulunamadı."
                    responsiveLayout="stack"
                    className="p-datatable-sm"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={() => 'hover:bg-slate-50 transition-colors'}
                  >
                    <Column field="date" header="Tarih" sortable className="text-slate-600 py-4 font-medium" />
                    <Column field="productName" header="Ürün" sortable className="text-slate-800 font-bold" />
                    <Column field="quantity" header="Adet" sortable body={(r) => <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-bold border border-slate-200">{r.quantity}</span>} />
                    <Column field="unitPrice" header="Birim Fiyat" sortable body={(r) => <span className="text-slate-600">₺{r.unitPrice.toLocaleString("tr-TR",{
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          })}</span>} />
                    <Column field="totalPrice" header="Toplam" sortable className="text-slate-900 font-black" body={(r) => `₺${r.totalPrice.toLocaleString("tr-TR",{
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          })}`} />
                    <Column
                      header="İşlem"
                      body={(r) => (
                        <Button
                          icon="pi pi-refresh"
                          className="p-button-rounded p-button-warning p-button-text hover:bg-orange-50"
                          onClick={() => {
                            setSelectedReturnRow(r);
                            setReturnQuantity(1);
                            setShowReturnDialog(true);
                          }}
                          tooltip="İade Et"
                          tooltipOptions={{ position: 'top' }}
                        />
                      )}
                      className="w-16"
                    />
                  </DataTable>
                </div>
              </TabPanel>
              <TabPanel header={<span><i className="pi pi-file mr-2"></i>Fatura Bazlı Alışlar</span>}>
                <div className="bg-white overflow-hidden">
                  <div className="flex items-center gap-2 p-3 bg-slate-50 border-b border-slate-100">
                    <Checkbox inputId="showOnlyUnpaid" onChange={e => setShowOnlyUnpaid(e.checked)} checked={showOnlyUnpaid} />
                    <label htmlFor="showOnlyUnpaid" className="text-xs font-bold text-slate-600 cursor-pointer">Sadece Ödenmemiş Faturaları Göster</label>
                  </div>
                  <DataTable
                    value={filteredInvoiceHistory}
                    paginator rows={10}
                    emptyMessage="Fatura bulunamadı."
                    responsiveLayout="stack"
                    className="p-datatable-sm supplier-invoice-table"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={(rowData) => {
                      const isUnpaid = (rowData.isDueTrackingActive || rowData.IsDueTrackingActive) &&
                                       (rowData.remainingAmount > 0 || rowData.RemainingAmount > 0);
                      return isUnpaid ? 'unpaid-invoice-row' : '';
                    }}
                  >
                    <Column field="date" header="Tarih" sortable className="text-slate-600 py-4 font-medium" />
                    <Column field="invoiceNo" header="Fatura No" sortable className="text-slate-800 font-bold" />
                    <Column field="totalQuantity" header="Toplam Ürün" sortable body={(r) => <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold border border-blue-100">{r.totalQuantity}</span>} />
                    <Column field="totalAmount" header="Toplam Tutar" sortable className="text-slate-900 font-black" body={(r) => `₺${r.totalAmount.toLocaleString()}`} />
                    <Column field="dueDate" header="Vade Tarihi" sortable className="text-slate-600 font-bold" body={(r) => (
                      <span className={r.isDueTrackingActive && new Date(r.dueDate.split('.').reverse().join('-')) < new Date() ? 'text-red-600' : ''}>
                        {r.dueDate}
                      </span>
                    )} />
                    <Column field="remainingAmount" header="Kalan" sortable className="text-slate-900 font-black" body={(r) => r.isDueTrackingActive ? `₺${r.remainingAmount.toLocaleString()}` : '-'} />
                    <Column
                      header="İşlem"
                      body={(r) => (
                        <div className="flex justify-center">
                           <Button
                            type="button"
                            icon="pi pi-ellipsis-v"
                            label="İşlem"
                            className="p-button-sm p-button-outlined p-button-secondary rounded-lg"
                            onClick={(e) => {
                              setSelectedInvoice(r);
                              menuRef.current.toggle(e);
                            }}
                            aria-controls="invoice_menu"
                            aria-haspopup
                          />
                        </div>
                      )}
                    />
                  </DataTable>
                </div>
              </TabPanel>
              <TabPanel header={<span><i className="pi pi-refresh mr-2"></i>İadeler</span>}>
                <div className="bg-white overflow-hidden">
                  <DataTable
                    value={returnHistory}
                    paginator rows={10}
                    emptyMessage="İade kaydı bulunamadı."
                    responsiveLayout="stack"
                    className="p-datatable-sm"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={(rowData) => ({
                      '!bg-red-50/80': rowData.isDueTrackingActive && rowData.remainingAmount > 0,
                      'hover:bg-slate-50 transition-colors': true
                    })}
                  >
                    <Column field="date" header="Tarih" sortable className="text-slate-600 py-4 font-medium" />
                    <Column field="invoiceNo" header="Fatura No" sortable className="text-slate-800 font-bold" />
                    <Column field="totalQuantity" header="Toplam Ürün" sortable body={(r) => <span className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-bold border border-orange-100">{r.totalQuantity}</span>} />
                    <Column field="totalAmount" header="Toplam Tutar" sortable className="text-slate-900 font-black" body={(r) => (
                      <span
                        className="cursor-help inline-block"
                        onMouseEnter={(e) => {
                          e.currentTarget.setAttribute(
                            "data-pr-tooltip",
                            `Kdv Dahil hesaplanmıştır. İade tutarı, iade edilen ürünlerin toplam fiyatının yanı sıra ilgili KDV tutarını da içermektedir.`
                          );

                          tooltipRef.current?.show(e);
                        }}
                        onMouseLeave={(e) => {
                          tooltipRef.current?.hide(e);
                        }}
                      >
                        ₺
                        {r.totalAmount?.toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    )}/>
                    <Column field="description" header="Açıklama" className="text-slate-500 text-sm" />
                    <Column
                        header="İşlem"
                        body={(r) => (
                            <Button
                                icon="pi pi-trash"
                                className="p-button-rounded p-button-text p-button-danger p-0 w-8 h-8"
                                onClick={() => handleDeleteReturn(r)}
                                tooltip="İadeyi İptal Et"
                                tooltipOptions={{ position: 'top' }}
                            />
                        )}
                        className="w-16"
                    />
                  </DataTable>
                </div>
              </TabPanel>
            </TabView>
          </Card>
        </div>

        {/* Ödeme Geçmişi SIDEBAR PANEL */}
        <div className="lg:col-span-4 bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden flex flex-col h-full">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="bg-emerald-500 w-1.5 h-5 rounded-full"></div>
                    <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Ödeme Geçmişi</h2>
                </div>
                <Tag value={`${paymentHistory.length} İŞLEM`} className="bg-emerald-600 text-[10px] font-black" />
            </div>

            <div className="p-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                    <span className="p-input-icon-left flex-1 min-w-[120px]">
                        <i className="pi pi-search text-xs" />
                        <InputText
                            type="search"
                            onInput={(e) => setPaymentFilters({...paymentFilters, global: { value: e.target.value, matchMode: "contains" }})}
                            placeholder="Ödeme ara..."
                            className="w-full rounded-lg text-xs py-2"
                        />
                    </span>
                    <Dropdown
                        value={paymentFilters.paymentMethod.value}
                        options={[{label: 'Tüm Yöntemler', value: null}, ...paymentMethods, {label: 'Bakiye Düzenleme', value: 'Bakiye Düzenleme'}]}
                        onChange={(e) => setPaymentFilters({...paymentFilters, paymentMethod: { value: e.value, matchMode: "equals" }})}
                        placeholder="Yöntem"
                        className="w-[110px] rounded-lg text-xs"
                        appendTo={mounted ? document.body : "self"}
                    />
                    <Calendar
                        value={paymentFilters.date.value ? (function(){
                            const p = paymentFilters.date.value.split('.');
                            return new Date(p[2], p[1]-1, p[0]);
                        })() : null}
                        onChange={(e) => {
                            if (!e.value) {
                                setPaymentFilters({...paymentFilters, date: { value: null, matchMode: "contains" }});
                                return;
                            }
                            const day = String(e.value.getDate()).padStart(2, '0');
                            const month = String(e.value.getMonth() + 1).padStart(2, '0');
                            const year = e.value.getFullYear();
                            const val = `${day}.${month}.${year}`;
                            setPaymentFilters({...paymentFilters, date: { value: val, matchMode: "contains" }});
                        }}
                        dateFormat="dd.mm.yy"
                        placeholder="Tarih"
                        className="w-[100px] rounded-lg text-xs"
                        locale="tr"
                        showButtonBar
                        appendTo={mounted ? document.body : "self"}
                    />
                </div>

                <DataTable
                    value={paymentHistory}
                    paginator rows={10}
                    filters={paymentFilters}
                    globalFilterFields={['date', 'description', 'amount', 'paymentMethod']}
                    emptyMessage="Ödeme bulunamadı."
                    responsiveLayout="scroll"
                    className="p-datatable-sm flex-1"
                    paginatorClassName="border-t border-slate-100 px-2 py-2"
                    rowClassName={() => 'hover:bg-emerald-50/20 transition-colors'}
                >
                    <Column field="date" header="TARİH" sortable className="text-slate-500 text-[10px] font-bold" />
                    <Column field="paymentMethod" header="YÖNTEM" sortable body={(r) => (
                         <div className="flex flex-col">
                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase w-fit ${r.paymentMethod === 'Bakiye Düzenleme' ? '!bg-orange-100 text-orange-700' : '!bg-emerald-100 text-emerald-700'}`}>
                                {r.paymentMethod}
                            </span>
                            <span className="text-[8px] text-slate-400 font-bold mt-0.5 italic truncate max-w-[100px]">{r.description}</span>
                        </div>
                    )} />
                    <Column field="amount" header="TUTAR" sortable body={(r) => <span className="font-black text-xs text-slate-900">₺{r.amount.toLocaleString("tr-TR",{
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          })}</span>} />
                    <Column
                        header="Aksiyon"
                        body={(r) => (
                            <div className="flex gap-1">
                                <Button
                                    icon="pi pi-pencil"
                                    className="p-button-rounded p-button-text p-button-warning p-0 w-8 h-8"
                                    onClick={() => {
                                        const dateParts = r.date.split('.');
                                        const parsedDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);

                                        let parsedDueDate = null;
                                        if (r.dueDate) {
                                            const dParts = r.dueDate.split('.');
                                            parsedDueDate = new Date(dParts[2], dParts[1] - 1, dParts[0]);
                                        }

                                        let rawMethod = r.paymentMethod || (r.description?.includes(' - ') ? r.description.split(' - ')[0].trim() : 'Nakit');

                                        // Map Endorsements to standard Çek/Senet buttons for the UI grid selection
                                        if (rawMethod === 'ChequeEndorse' || rawMethod === 'Çek Cirosu') rawMethod = 'Çek';
                                        if (rawMethod === 'PromissoryEndorse' || rawMethod === 'Senet Cirosu') rawMethod = 'Senet';

                                        const matchedMethod = paymentMethods.find(m => m.value === rawMethod || m.label === rawMethod)?.value || 'Nakit';

                                        // Clean up description (remove extra info blocks to avoid duplication)
                                        let cleanDesc = r.description?.includes(' - ') ? r.description.split(' - ').slice(1).join(' - ') : (r.description || '');
                                        cleanDesc = cleanDesc.split('\n[')[0].trim();

                                        setEditingPayment({
                                            id: r.id,
                                            amount: Math.abs(r.amount),
                                            paymentDate: parsedDate,
                                            description: cleanDesc,
                                            paymentMethod: matchedMethod,
                                            financialAccountId: r.financialAccountId,
                                            bankName: r.bankName,
                                            bankBranch: r.bankBranch,
                                            checkNumber: r.checkNumber,
                                            dueDate: parsedDueDate,
                                            drawer: r.drawer,
                                            guarantor: r.guarantor
                                        });
                                        setPaymentMethod(matchedMethod);
                                        setShowEditPaymentDialog(true);
                                    }}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-rounded p-button-text p-button-danger p-0 w-8 h-8"
                                    onClick={() => handleDeletePayment(r)}
                                />
                            </div>
                        )}
                    />
                </DataTable>
            </div>
        </div>
      </div>

      {/* INVOICE DETAIL DIALOG */}
      <Dialog
        header={
          <div className="flex items-center gap-2">
            <i className="pi pi-file text-blue-500"></i>
            <span>Fatura Detayı {invoiceDetail?.invoiceNo ? `- ${invoiceDetail.invoiceNo}` : ""}</span>
          </div>
        }
        visible={detailDialogVisible}
        onHide={() => {
          setDetailDialogVisible(false);
          setInvoiceDetail(null);
        }}
        style={{ width: "700px" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        className="shadow-xl rounded-xl"
        appendTo={mounted ? document.body : "self"}
      >
        {loadingDetail ? (
          <div className="p-4">
            <Skeleton height="2rem" className="mb-2" />
            <Skeleton height="10rem" />
          </div>
        ) : invoiceDetail ? (
          <div className="space-y-4">
             <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">FATURA NO</span>
                    <span className="font-bold text-slate-700">{invoiceDetail.invoiceNo}</span>
                </div>
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">TARİH</span>
                    <span className="font-bold text-slate-700">{new Date(invoiceDetail.purchaseDate).toLocaleDateString("tr-TR")}</span>
                </div>
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">ÖDEME TİPİ</span>
                    <span className="font-bold text-slate-700">{invoiceDetail.paymentType === 1 ? "Nakit" : "Vadeli"}</span>
                </div>
                <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">TOPLAM TUTAR</span>
                    <span className="font-bold text-emerald-600">₺{invoiceDetail.totalInclVat.toLocaleString("tr-TR")}</span>
                </div>
             </div>

             <DataTable
                value={invoiceDetail.lines}
                className="p-datatable-sm"
                responsiveLayout="scroll"
                emptyMessage="Ürün bulunamadı."
             >
                <Column field="productName" header="Ürün Adı" className="font-bold" />
                <Column field="quantity" header="Miktar" body={(r) => `${r.quantity} ${r.unit}`} />
                <Column field="unitPrice" header="Birim Fiyat (KDV Hariç)" body={(r) => `₺${r.unitPrice.toLocaleString("tr-TR")}`} />
                <Column header="Toplam" body={(r) => `₺${(r.quantity * r.unitPrice).toLocaleString("tr-TR")}`} className="font-black text-slate-900" />
             </DataTable>

             {invoiceDetail.description && (
                <div className="p-3 bg-blue-50 text-blue-700 rounded-lg text-sm border border-blue-100">
                    <i className="pi pi-info-circle mr-2"></i>
                    {invoiceDetail.description}
                </div>
             )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">Detay yüklenemedi.</div>
        )}
      </Dialog>

      {/* MODERN EDIT PAYMENT SIDEBAR */}
      <Sidebar
        visible={showEditPaymentDialog}
        onHide={() => setShowEditPaymentDialog(false)}
        position="right"
        style={{ width: `${sidebarWidth}px`, maxWidth: '100vw' }}
        className="p-0 border-none shadow-2xl relative"
        content={({ hide }) => (
            <div className="h-full flex flex-col bg-slate-50 relative">
                {/* Resize Handle */}
                <div
                  onMouseDown={startResizing}
                  className="absolute left-0 top-0 w-1 h-full cursor-col-resize hover:bg-amber-500/30 transition-colors z-[100] group"
                >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-8 bg-slate-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>

                {/* Header Section */}
                <div className="bg-slate-900 h-1/5 py-3 px-8 relative overflow-hidden shrink-0 flex flex-col justify-center">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full -mr-24 -mt-24 blur-3xl"></div>
                    <button onClick={hide} 
                    className="absolute top-3 right-6 z-20  rounded-full border border-white/10 bg-white/5 text-white/70 backdrop-blur-md hover:bg-white/30 hover:text-white hover:border-white/20 transition-all duration-200"
                    aria-label="Kapat"
                    ><i className="pi pi-times text-lg"></i></button>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-2 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                                <i className="pi pi-pencil text-white text-lg"></i>
                            </div>
                            <div>
                                <span className="text-[9px] font-black text-amber-500 uppercase tracking-[0.2em] block">ÖDEME DÜZENLE</span>
                                <h2 className="text-xl font-black text-white leading-tight truncate max-w-[400px]">{supplier?.name}</h2>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">GÜNCEL BAKİYE</span>
                                <div className="text-lg font-black text-white">{(supplier?.balance || 0).toLocaleString()} <span className="text-amber-500 text-xs">₺</span></div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">YENİ BAKİYE</span>
                                <div className="text-lg font-black text-white">
                                    {( (supplier?.balance || 0) + (paymentHistory.find(p => p.id === editingPayment?.id)?.amount || 0) - (editingPayment?.amount || 0)).toLocaleString()}
                                    <span className="text-amber-500 text-xs"> ₺</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body Content */}
                <div className="h-3/5 overflow-y-auto custom-scrollbar px-8 py-6 space-y-8 flex-1">
                    {editingPayment && (
                        <>
                            {/* Payment Method Selector with Categories */}
                            <section className="space-y-6">
                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <i className="pi pi-th-large text-amber-500"></i> ÖDEME YÖNTEMİ SEÇİN
                                </label>

                                {["Likit", "Dijital", "Evrak"].map(cat => (
                                    <div key={cat} className="space-y-3">
                                        <div className="flex items-center gap-3 px-1">
                                            <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.1em]">{cat}</span>
                                            <div className="flex-1 h-px bg-slate-400"></div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {paymentMethods
                                                .filter(m => m.category === cat )
                                                .map((m) => (
                                                <button
                                                    key={m.value}
                                                    onClick={() => {
                                                        setEditingPayment({...editingPayment, paymentMethod: m.value});
                                                        setPaymentMethod(m.value);
                                                    }}
                                                    className={`group relative flex items-center gap-3 p-3 rounded-2xl border-2 transition-all duration-300 text-left ${
                                                        editingPayment.paymentMethod === m.value
                                                        ? 'border-amber-500 bg-white shadow-xl shadow-amber-500/5'
                                                        : 'border-slate-200 bg-white hover:border-slate-500'
                                                    }`}
                                                >
                                                    <div className={`w-4 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                                                        editingPayment.paymentMethod === m.value
                                                        ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20'
                                                        : 'bg-slate-200 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-500'
                                                    }`}>
                                                        <i className={`pi ${m.icon} text-lg`}></i>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-0.5">
                                                            <span className={`text-[12px] font-black uppercase tracking-tight leading-none ${editingPayment.paymentMethod === m.value ? 'text-slate-900' : 'text-slate-600'}`}>
                                                                {m.label}
                                                            </span>
                                                            {editingPayment.paymentMethod === m.value && (
                                                                <i className="pi pi-check-circle text-amber-500 text-xs animate-scale-in"></i>
                                                            )}
                                                        </div>
                                                        <p className={`text-[10px] leading-tight font-medium truncate ${editingPayment.paymentMethod === m.value ? 'text-amber-600/80' : 'text-slate-400'}`}>
                                                            {m.desc}
                                                        </p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </section>

                            {/* Document Details for Edit */}
                            {(editingPayment.paymentMethod === "Çek" || editingPayment.paymentMethod === "Senet") && (
                                <section className="bg-amber-50/50 p-6 rounded-3xl space-y-5 border border-amber-100 animate-fade-in">
                                    <div className="text-[11px] font-black uppercase tracking-widest text-amber-700 flex items-center gap-2">
                                        <i className="pi pi-file-edit"></i> BELGE DETAYLARI
                                    </div>
                                    <div className="space-y-4">
                                        {editingPayment.paymentMethod === 'Çek' ? (
                                            <>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Banka Adı</label>
                                                        <InputText value={editingPayment.bankName || ''} onChange={e => setEditingPayment({...editingPayment, bankName: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Şube</label>
                                                        <InputText value={editingPayment.bankBranch || ''} onChange={e => setEditingPayment({...editingPayment, bankBranch: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade Tarihi</label>
                                                        <Calendar value={editingPayment.dueDate} onChange={e => setEditingPayment({...editingPayment, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Çek Numarası</label>
                                                        <InputText value={editingPayment.checkNumber || ''} onChange={e => setEditingPayment({...editingPayment, checkNumber: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Keşideci</label>
                                                    <InputText value={editingPayment.drawer || ''} onChange={e => setEditingPayment({...editingPayment, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Borçlu Adı</label>
                                                    <InputText value={editingPayment.drawer || ''} onChange={e => setEditingPayment({...editingPayment, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade Tarihi</label>
                                                        <Calendar value={editingPayment.dueDate} onChange={e => setEditingPayment({...editingPayment, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1">Kefil</label>
                                                        <InputText value={editingPayment.guarantor || ''} onChange={e => setEditingPayment({...editingPayment, guarantor: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </section>
                            )}

                            {/* Account & Date Section */}
                            <section className="grid grid-cols-1 gap-6">
                                {filteredAccounts.length > 0 && (
                                    <div className="space-y-2">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <i className="pi pi-credit-card text-amber-500"></i> HESAP SEÇİMİ
                                        </label>
                                        <Dropdown
                                            value={editingPayment.financialAccountId}
                                            options={filteredAccounts}
                                            optionLabel="name"
                                            optionValue="id"
                                            onChange={(e) => setEditingPayment({...editingPayment, financialAccountId: e.value})}
                                            placeholder="Hesap seçin..."
                                            className="w-full rounded-2xl border-slate-200 font-bold"
                                            appendTo={mounted ? document.body : "self"}
                                        />
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <i className="pi pi-calendar text-amber-500"></i> İŞLEM TARİHİ
                                    </label>
                                    <Calendar
                                        value={editingPayment.paymentDate}
                                        onChange={(e) => setEditingPayment({...editingPayment, paymentDate: e.value})}
                                        dateFormat="dd.mm.yy"
                                        locale="tr"
                                        showIcon
                                        appendTo={mounted ? document.body : "self"}
                                        className="w-full"
                                        inputClassName="font-bold py-3 rounded-2xl border-slate-200"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        <i className="pi pi-align-left text-amber-500"></i> AÇIKLAMA
                                    </label>
                                    <InputTextarea
                                        value={editingPayment.description}
                                        onChange={(e) => setEditingPayment({...editingPayment, description: e.target.value})}
                                        placeholder="Opsiyonel açıklama..."
                                        rows={2}
                                        className="w-full p-4 rounded-2xl border-slate-200 font-medium text-sm"
                                        autoResize
                                    />
                                </div>
                            </section>
                        </>
                    )}
                </div>

                {/* Fixed Footer */}
                <div className="h-1/5 px-8 py-3 bg-white border-t border-slate-100 space-y-3 shrink-0 flex flex-col justify-center">
                    <div className="relative">
                        <span className="absolute -top-2 left-5 px-2 bg-white text-[9px] font-black text-amber-600 uppercase tracking-widest z-10 rounded-full border border-amber-100">ÖDEME TUTARI</span>
                        <InputNumber
                            value={editingPayment?.amount}
                            onValueChange={(e) => setEditingPayment({...editingPayment, amount: e.value})}
                            mode="decimal"
                            minFractionDigits={2}
                            className="w-full"
                            inputClassName="font-black text-2xl py-4 px-6 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:border-amber-500 transition-all text-slate-900"
                            placeholder="0.00"
                        />
                        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-xl font-black text-slate-300 pointer-events-none">₺</div>
                    </div>
                    <Button
                        label="DEĞİŞİKLİKLERİ KAYDET"
                        icon="pi pi-check-circle"
                        className="w-full bg-slate-900 hover:bg-black border-none py-4 rounded-2xl font-black text-xs tracking-widest shadow-xl shadow-slate-900/20 text-white transition-all transform hover:scale-[1.02] active:scale-95"
                        onClick={handleUpdatePayment}
                        loading={submittingPayment}
                        disabled={!editingPayment || editingPayment.amount <= 0}
                    />
                </div>
            </div>
        )}
      />

      {/* MODERN NEW PAYMENT SIDEBAR */}
      <Sidebar
        visible={paymentDialogVisible}
        onHide={() => setPaymentDialogVisible(false)}
        position="right"
        style={{ width: `${sidebarWidth}px`, maxWidth: '100vw' }}
        className="p-0 border-none shadow-2xl relative"
        onShow={() => setTimeout(() => paymentAmountRef.current?.focus(), 50)}
        content={({ hide }) => (
            <div className="h-full flex flex-col bg-slate-50 relative">
                {/* Resize Handle */}
                <div
                  onMouseDown={startResizing}
                  className="absolute left-0 top-0 w-1 h-full cursor-col-resize hover:bg-emerald-500/30 transition-colors z-[100] group"
                >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-8 bg-slate-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>

                {/* Header Section */}
                <div className="bg-slate-900 h-1/5 py-3 px-8 relative overflow-hidden shrink-0 flex flex-col justify-center">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -mr-24 -mt-24 blur-3xl"></div>
                    <button onClick={hide} 
                    className="absolute top-3 right-6 z-20  rounded-full border border-white/10 bg-white/5 text-white/70 backdrop-blur-md hover:bg-white/30 hover:text-white hover:border-white/20 transition-all duration-200"
                    aria-label="Kapat"
                    
                    ><i className="pi pi-times text-lg"></i></button>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-2 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-xl shadow-emerald-500/20">
                                <i className="pi pi-wallet text-white text-lg"></i>
                            </div>
                            <div>
                                <span className="text-[9px] font-black text-emerald-500 uppercase tracking-[0.2em] block">YENİ ÖDEME</span>
                                <h2 className="text-xl font-black text-white leading-tight truncate max-w-[400px]">{supplier?.name}</h2>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">GÜNCEL BAKİYE</span>
                                <div className="text-lg font-black text-white">{(supplier?.balance || 0).toLocaleString()} <span className="text-emerald-500 text-xs">₺</span></div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">PROJEKSİYON</span>
                                <div className="text-lg font-black text-white">
                                    {((supplier?.balance || 0) - (paymentAmount || 0)).toLocaleString()}
                                    <span className="text-emerald-500 text-xs"> ₺</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body Content */}
                <div className="h-3/5 overflow-y-auto custom-scrollbar px-8 py-6 space-y-8 flex-1">
                    {/* Payment Method Selector with Categories */}
                    <section className="space-y-6">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <i className="pi pi-th-large text-emerald-500"></i> ÖDEME YÖNTEMİ SEÇİN
                        </label>

                        {["Likit", "Dijital", "Evrak"].map(cat => (
                            <div key={cat} className="space-y-3">
                                <div className="flex items-center gap-3 px-1">
                                    <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.1em]">{cat}</span>
                                    <div className="flex-1 h-px bg-slate-400"></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {paymentMethods
                                        .filter(m => m.category === cat)
                                        .map((m) => (
                                        <button
                                            key={m.value}
                                            onClick={() => {
                                                setPaymentMethod(m.value);
                                                setUseExistingDocument(false);
                                                setSelectedDocument(null);
                                            }}
                                            className={`group relative flex items-center gap-3 p-3 rounded-2xl border-2 transition-all duration-300 text-left ${
                                                paymentMethod === m.value
                                                ? 'border-emerald-500 bg-white shadow-xl shadow-emerald-500/5'
                                                : 'border-slate-200 bg-white hover:border-slate-500'
                                            }`}
                                        >
                                            <div className={`w-4 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                                                paymentMethod === m.value
                                                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                                                : 'bg-slate-200 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-500'
                                            }`}>
                                                <i className={`pi ${m.icon} text-lg`}></i>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-0.5">
                                                    <span className={`text-[12px] font-black uppercase tracking-tight leading-none ${paymentMethod === m.value ? 'text-slate-900' : 'text-slate-600'}`}>
                                                        {m.label}
                                                    </span>
                                                    {paymentMethod === m.value && (
                                                        <i className="pi pi-check-circle text-emerald-500 text-xs animate-scale-in"></i>
                                                    )}
                                                </div>
                                                <p className={`text-[10px] leading-tight font-medium truncate ${paymentMethod === m.value ? 'text-emerald-600/80' : 'text-slate-400'}`}>
                                                    {m.desc}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>

                    {/* Document Details Logic */}
                    {(paymentMethod === "ChequeEndorse" || paymentMethod === "PromissoryEndorse" || paymentMethod === "Çek" || paymentMethod === "Senet") && (
                        <section className="bg-emerald-50/50 p-6 rounded-3xl space-y-5 border border-emerald-100 animate-fade-in">
                            <div className="flex justify-between items-center">
                                <div className="text-[11px] font-black uppercase tracking-widest text-emerald-700 flex items-center gap-2">
                                    <i className="pi pi-file-edit"></i> BELGE BİLGİSİ
                                </div>
                                {(paymentMethod === "Çek" || paymentMethod === "Senet") && (
                                    <SelectButton
                                        value={useExistingDocument ? 'existing' : 'new'}
                                        options={[{label: 'YENİ', value: 'new'}, {label: 'PORTFÖY', value: 'existing'}]}
                                        onChange={(e) => { if (e.value) { setUseExistingDocument(e.value === 'existing'); setSelectedDocument(null); } }}
                                        className="custom-selectbutton modern-small shadow-sm"
                                    />
                                )}
                            </div>

                            {((paymentMethod === "ChequeEndorse" || paymentMethod === "PromissoryEndorse") || useExistingDocument) ? (
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">PORTFÖYDEN SEÇ</label>
                                    <Dropdown
                                        value={selectedDocument}
                                        onChange={(e) => { setSelectedDocument(e.value); setPaymentAmount(e.value.amount); }}
                                        options={paymentMethod.includes('Cheque') || paymentMethod === 'Çek' ? (paymentMethod === 'Çek' ? ownPortfolioCheques : portfolioCheques) : (paymentMethod === 'Senet' ? ownPortfolioNotes : portfolioNotes)}
                                        optionLabel={(option) => `${option.bankName || option.drawer} - ${option.amount.toLocaleString()} ₺`}
                                        placeholder="Belge seçin..."
                                        className="w-full rounded-xl border-slate-200 font-bold"
                                        appendTo={mounted ? document.body : "self"}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {paymentMethod === 'Çek' ? (
                                        <>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Banka</label><InputText value={chequeDetails.bankName} onChange={e => setChequeDetails({...chequeDetails, bankName: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Şube</label><InputText value={chequeDetails.bankBranch} onChange={e => setChequeDetails({...chequeDetails, bankBranch: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade</label><Calendar value={chequeDetails.dueDate} onChange={e => setChequeDetails({...chequeDetails, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" /></div>
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">No</label><InputText value={chequeDetails.checkNumber} onChange={e => setChequeDetails({...chequeDetails, checkNumber: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            </div>
                                            <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Keşideci</label><InputText value={chequeDetails.drawer} onChange={e => setChequeDetails({...chequeDetails, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Borçlu</label><InputText value={promissoryDetails.drawer} onChange={e => setPromissoryDetails({...promissoryDetails, drawer: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Vade</label><Calendar value={promissoryDetails.dueDate} onChange={e => setPromissoryDetails({...promissoryDetails, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={mounted ? document.body : "self"} className="w-full" inputClassName="py-3 rounded-xl border-slate-200 font-bold" /></div>
                                                <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase ml-1">Kefil</label><InputText value={promissoryDetails.guarantor} onChange={e => setPromissoryDetails({...promissoryDetails, guarantor: e.target.value})} className="w-full py-3 rounded-xl border-slate-200 font-bold" /></div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    {/* Account & Date Section */}
                    <section className="grid grid-cols-1 gap-6">
                        {filteredAccounts.length > 0 && (
                            <div className="space-y-2">
                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <i className="pi pi-credit-card text-emerald-500"></i> HESAP SEÇİMİ
                                </label>
                                <Dropdown
                                    value={selectedAccountId}
                                    options={filteredAccounts}
                                    optionLabel="name"
                                    optionValue="id"
                                    onChange={(e) => setSelectedAccountId(e.value)}
                                    placeholder="İşlem yapılacak hesabı seçin"
                                    className="w-full rounded-2xl border-slate-200 font-bold"
                                    appendTo={mounted ? document.body : "self"}
                                />
                            </div>
                        )}
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <i className="pi pi-calendar text-emerald-500"></i> İŞLEM TARİHİ
                            </label>
                            <Calendar
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.value)}
                                dateFormat="dd.mm.yy"
                                locale="tr"
                                showIcon
                                appendTo={mounted ? document.body : "self"}
                                className="w-full"
                                inputClassName="font-bold py-3 rounded-2xl border-slate-200"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <i className="pi pi-align-left text-emerald-500"></i> AÇIKLAMA
                            </label>
                            <InputTextarea
                                value={paymentDescription}
                                onChange={(e) => setPaymentDescription(e.target.value)}
                                placeholder="Opsiyonel açıklama..."
                                rows={2}
                                className="w-full p-4 rounded-2xl border-slate-200 font-medium text-sm"
                                autoResize
                            />
                        </div>
                    </section>
                </div>

                {/* Fixed Footer */}
                <div className="h-1/5 px-8 py-3 bg-white border-t border-slate-100 space-y-3 shrink-0 flex flex-col justify-center">
                    <div className="relative">
                        <span className="absolute -top-2 left-5 px-2 bg-white text-[9px] font-black text-emerald-600 uppercase tracking-widest z-10 rounded-full border border-emerald-100">ÖDEME TUTARI</span>
                        <InputNumber
                            inputRef={paymentAmountRef}
                            value={paymentAmount}
                            onValueChange={(e) => setPaymentAmount(e.value)}
                            mode="decimal"
                            minFractionDigits={2}
                            className="w-full"
                            inputClassName="font-black text-2xl py-3 px-6 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:border-emerald-500 transition-all text-slate-900"
                            placeholder="0.00"
                        />
                        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-xl font-black text-slate-300 pointer-events-none">₺</div>
                    </div>
                    <Button
                        label="İŞLEMİ ONAYLA"
                        icon="pi pi-check-circle"
                        className="w-full bg-slate-900 hover:bg-black border-none py-4 rounded-2xl font-black text-xs tracking-widest shadow-xl shadow-slate-900/20 text-white transition-all transform hover:scale-[1.02] active:scale-95"
                        onClick={handlePayment}
                        loading={submittingPayment}
                        disabled={paymentAmount <= 0 && paymentMethod !== "ChequeEndorse" && paymentMethod !== "PromissoryEndorse" && !useExistingDocument}
                    />
                </div>
            </div>
        )}
      />
    </div>
  );
}
